import { create } from 'zustand';
import {
  resolutionApi,
  type RawConceptData,
  type ResolutionResultData,
  type ResolutionReviewStats,
} from '@/services/api';

type OrderBy = 'confidence_asc' | 'confidence_desc' | 'created_desc' | 'created_asc';

interface ReviewFilters {
  human_reviewed: boolean;
  decision: string;
  entity_type: string;
  order_by: OrderBy;
}

interface ReviewQueueState {
  results: ResolutionResultData[];
  total: number;
  stats: ResolutionReviewStats | null;
  filters: ReviewFilters;
  selectedId: string | null;
  sourceConcepts: Record<string, RawConceptData>;
  loading: boolean;
  conceptsLoading: boolean;
  patching: boolean;
  error: string | null;

  setFilters: (partial: Partial<ReviewFilters>) => void;
  fetchQueue: () => Promise<void>;
  fetchStats: () => Promise<void>;
  selectResult: (id: string | null) => void;
  submitVerdict: (
    verdict: 'confirmed' | 'overridden' | 'flagged',
    opts: { note?: string; reviewerEmail?: string },
  ) => Promise<void>;
}

export const useReviewQueueStore = create<ReviewQueueState>((set, get) => ({
  results: [],
  total: 0,
  stats: null,
  filters: {
    human_reviewed: false,
    decision: '',
    entity_type: '',
    order_by: 'confidence_asc',
  },
  selectedId: null,
  sourceConcepts: {},
  loading: false,
  conceptsLoading: false,
  patching: false,
  error: null,

  setFilters: (partial) => {
    set((s) => ({ filters: { ...s.filters, ...partial } }));
    get().fetchQueue();
  },

  fetchQueue: async () => {
    const { filters } = get();
    set({ loading: true, error: null });
    try {
      const res = await resolutionApi.listResults({
        human_reviewed: filters.human_reviewed,
        decision: filters.decision || undefined,
        entity_type: filters.entity_type || undefined,
        order_by: filters.order_by,
        limit: 100,
      });
      const currentSelected = get().selectedId;
      const stillPresent = res.results.find((r) => r.id === currentSelected);
      set({
        results: res.results,
        total: res.total,
        loading: false,
        selectedId: stillPresent
          ? currentSelected
          : res.results[0]?.id ?? null,
      });
      // Kick off source-concept loading for the selected item.
      const nextId = stillPresent ? currentSelected : res.results[0]?.id;
      if (nextId) {
        void loadSourceConcepts(get, set, nextId);
      }
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  fetchStats: async () => {
    try {
      const stats = await resolutionApi.getReviewStats();
      set({ stats });
    } catch {
      /* non-fatal */
    }
  },

  selectResult: (id) => {
    set({ selectedId: id });
    if (id) void loadSourceConcepts(get, set, id);
  },

  submitVerdict: async (verdict, opts) => {
    const { selectedId, results, filters } = get();
    if (!selectedId) return;
    set({ patching: true });
    try {
      const updated = await resolutionApi.patchResult(
        selectedId,
        {
          human_verdict: verdict,
          human_note: opts.note || undefined,
          reviewed_by: opts.reviewerEmail || undefined,
        },
        opts.reviewerEmail,
      );

      // If current filter is human_reviewed=false, drop the row and advance.
      if (!filters.human_reviewed) {
        const idx = results.findIndex((r) => r.id === selectedId);
        const nextList = results.slice();
        nextList.splice(idx, 1);
        const nextId = nextList[Math.min(idx, nextList.length - 1)]?.id ?? null;
        set({
          results: nextList,
          total: Math.max(0, get().total - 1),
          selectedId: nextId,
        });
        if (nextId) void loadSourceConcepts(get, set, nextId);
      } else {
        // Otherwise, patch the row in place.
        set({
          results: results.map((r) =>
            r.id === selectedId ? { ...r, ...updated } : r,
          ),
        });
      }

      // Refresh stats so header counters stay in sync.
      get().fetchStats();
    } catch (err) {
      set({ error: (err as Error).message });
    } finally {
      set({ patching: false });
    }
  },
}));

async function loadSourceConcepts(
  get: () => ReviewQueueState,
  set: (s: Partial<ReviewQueueState>) => void,
  resultId: string,
) {
  const result = get().results.find((r) => r.id === resultId);
  if (!result) return;

  const missing = result.source_entity_ids.filter(
    (id) => !get().sourceConcepts[id],
  );
  if (missing.length === 0) return;

  set({ conceptsLoading: true });
  const fetched: Record<string, RawConceptData> = {};
  const settled = await Promise.allSettled(
    missing.map((id) => resolutionApi.getConcept(id)),
  );
  settled.forEach((r, i) => {
    if (r.status === 'fulfilled') {
      fetched[missing[i]] = r.value;
    }
  });
  set({
    sourceConcepts: { ...get().sourceConcepts, ...fetched },
    conceptsLoading: false,
  });
}
